import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from "@mantis-sas/ui";
import { X, Save, Edit } from "lucide-react";
import { updateInitialPurchaseRequestField } from "../../../../ApiCalls/mantisFlowsManangment";

const EditIPRModal = ({ 
  isOpen, 
  onClose, 
  data, 
  clientId, 
  onUpdate 
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && data) {
      setFormData({
        title: data.title || '',
        justification: data.justification || '',
        area: data.area || '',
        amount: data.amount || '',
        currency: data.currency || 'USD'
      });
    }
  }, [isOpen, data]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!clientId || !data?.id) {
      toast({
        title: "Error",
        description: "Faltan datos necesarios para actualizar el IPR",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Update each field that has changed
      const updatePromises = [];
      const updatedData = { ...data }; // Create a copy of the current data
      
      Object.keys(formData).forEach(fieldName => {
        if (formData[fieldName] !== (data[fieldName] || '')) {
          updatePromises.push(
            updateInitialPurchaseRequestField(
              clientId,
              data.id,
              fieldName,
              formData[fieldName]
            )
          );
          // Update the local data copy immediately
          updatedData[fieldName] = formData[fieldName];
        }
      });

      if (updatePromises.length === 0) {
        toast({
          title: "Información",
          description: "No hay cambios para guardar",
        });
        onClose();
        return;
      }

      await Promise.all(updatePromises);

      toast({
        title: "Éxito",
        description: "IPR actualizado correctamente",
        variant: "default",
      });

      // Call the update callback with the updated data to refresh the parent component
      if (onUpdate) {
        onUpdate(updatedData);
      }

      onClose();
    } catch (error) {
      console.error("Error updating IPR:", error);
      toast({
        title: "Error",
        description: `Error al actualizar el IPR: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <Edit className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  Editar IPR
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  IPR #{data?.id || 'N/A'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium">
                Título del IPR
              </Label>
              <Input
                id="title"
                value={formData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Ingresa el título del IPR"
                className="w-full"
              />
            </div>

            {/* Justification */}
            <div className="space-y-2">
              <Label htmlFor="justification" className="text-sm font-medium">
                Justificación
              </Label>
              <Textarea
                id="justification"
                value={formData.justification || ''}
                onChange={(e) => handleInputChange('justification', e.target.value)}
                placeholder="Describe la justificación para esta solicitud"
                className="w-full min-h-[100px]"
              />
            </div>

            {/* Area */}
            <div className="space-y-2">
              <Label htmlFor="area" className="text-sm font-medium">
                Área/Departamento
              </Label>
              <Input
                id="area"
                value={formData.area || ''}
                onChange={(e) => handleInputChange('area', e.target.value)}
                placeholder="Ingresa el área o departamento"
                className="w-full"
              />
            </div>

            {/* Amount and Currency */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount" className="text-sm font-medium">
                  Monto
                </Label>
                <Input
                  id="amount"
                  type="number"
                  value={formData.amount || ''}
                  onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency" className="text-sm font-medium">
                  Moneda
                </Label>
                <Select
                  value={formData.currency || 'USD'}
                  onValueChange={(value) => handleInputChange('currency', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona moneda" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="COP">COP</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="MXN">MXN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 mt-8">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={isLoading}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Guardando...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Save className="h-4 w-4" />
                  <span>Guardar Cambios</span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditIPRModal;
